import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Avatar, Grid, Chip, Button, IconButton,
  List, ListItem, ListItemText, ListItemIcon, Divider, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert
} from '@mui/material';
import {
  ArrowBack, Edit, Phone, Email, Badge, Schedule, Work,
  AccessTime, EventAvailable, Save, Delete
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO, isToday, startOfWeek, endOfWeek } from 'date-fns';
import api from '../../api/axios';
import LoadingState from '../shared/LoadingState';

const STATUS_COLORS = {
  'New Request': '#64748B', 'Needs Info': '#9CA3AF', 'Tentative': '#3B82F6',
  'Locked': '#EAB308', 'Confirmed': '#22C55E', 'Scheduled': '#3B82F6',
  'Failed': '#EF4444', 'Completed': '#8B5CF6', 'Waiting on Client': '#F97316',
  'En Route': '#06B6D4', 'On Site': '#4ADE80', 'Cancelled': '#9CA3AF'
};

const WorkerDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [worker, setWorker] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [workerRes, jobsRes] = await Promise.all([
        api.get(`/admin/employees/${id}`),
        api.get('/admin/jobs').catch(() => ({ data: [] }))
      ]);
      setWorker(workerRes.data);
      setEditForm({
        name: workerRes.data.name || '',
        email: workerRes.data.email || '',
        phone_number: workerRes.data.phone_number || '',
        role: workerRes.data.role || 'worker'
      });
      // Filter jobs assigned to this worker
      const workerJobs = (jobsRes.data || []).filter(j => j.employee_id === parseInt(id));
      setJobs(workerJobs);
    } catch (err) {
      console.error('Failed to load worker:', err);
      setError('Failed to load worker details');
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/employees/${id}`, editForm);
      setEditing(false);
      loadData();
    } catch (err) {
      console.error('Failed to update worker:', err);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to remove ${worker.name}?`)) return;
    try {
      await api.delete(`/admin/employees/${id}`);
      navigate('/team');
    } catch (err) {
      console.error('Failed to delete worker:', err);
    }
  };

  if (loading) return <LoadingState message="Loading worker details..." />;
  if (error) return <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>;
  if (!worker) return <Box sx={{ p: 4 }}><Typography>Worker not found</Typography></Box>;

  const todayJobs = jobs.filter(j => j.scheduled_date && isToday(parseISO(j.scheduled_date)));
  const upcomingJobs = jobs.filter(j => 
    j.scheduled_date && 
    new Date(j.scheduled_date) > new Date() && 
    !['Completed', 'Failed', 'Cancelled'].includes(j.status)
  ).slice(0, 10);
  const completedCount = jobs.filter(j => j.status === 'Completed').length;
  const initials = (worker.name || '?').split(' ').map(n => n[0]).join('');

  return (
    <Box sx={{ pb: 10, maxWidth: 800, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <IconButton onClick={() => navigate('/team')}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" fontWeight={800}>Team Member</Typography>
      </Box>

      {/* Profile Card */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: '1.5rem', fontWeight: 700 }}>
            {initials}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" fontWeight={800}>{worker.name}</Typography>
            <Chip 
              label={worker.role || 'worker'} 
              size="small" 
              sx={{ mt: 0.5, textTransform: 'capitalize', fontWeight: 600 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={() => setEditing(true)} color="primary">
              <Edit />
            </IconButton>
            <IconButton onClick={handleDelete} color="error">
              <Delete />
            </IconButton>
          </Box>
        </Box>

        <Grid container spacing={2}>
          {worker.email && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                <Email fontSize="small" />
                <Typography variant="body2">{worker.email}</Typography>
              </Box>
            </Grid>
          )}
          {worker.phone_number && (
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                <Phone fontSize="small" />
                <Typography variant="body2">{worker.phone_number}</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Stats Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0', textAlign: 'center' }}>
            <Typography variant="h4" fontWeight={800} color="primary.main">{todayJobs.length}</Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Today</Typography>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0', textAlign: 'center' }}>
            <Typography variant="h4" fontWeight={800} color="info.main">{upcomingJobs.length}</Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Upcoming</Typography>
          </Paper>
        </Grid>
        <Grid item xs={4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0', textAlign: 'center' }}>
            <Typography variant="h4" fontWeight={800} color="success.main">{completedCount}</Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Completed</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Today's Jobs */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0', mb: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          <Work sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
          Today's Jobs
        </Typography>
        {todayJobs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No jobs scheduled today</Typography>
        ) : (
          <List disablePadding>
            {todayJobs.map((job, i) => (
              <React.Fragment key={job.id}>
                {i > 0 && <Divider />}
                <ListItem 
                  disablePadding 
                  sx={{ py: 1.5, cursor: 'pointer' }}
                  onClick={() => navigate(`/jobs/${job.id}`)}
                >
                  <ListItemIcon sx={{ minWidth: 12, mr: 2 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_COLORS[job.status] || '#9CA3AF' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight={600}>{job.location || 'No address'}</Typography>}
                    secondary={`${job.scheduled_date ? format(parseISO(job.scheduled_date), 'h:mm a') : '--'} • ${job.description || 'Job'}`}
                  />
                  <Chip 
                    label={job.status} 
                    size="small" 
                    sx={{ 
                      fontSize: '0.7rem', fontWeight: 600,
                      bgcolor: STATUS_COLORS[job.status] || '#9CA3AF', color: 'white' 
                    }} 
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Upcoming Schedule */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0', mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>
            <Schedule sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
            Upcoming Schedule
          </Typography>
          <Button size="small" onClick={() => navigate('/schedule')} sx={{ fontWeight: 600 }}>
            View Calendar
          </Button>
        </Box>
        {upcomingJobs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No upcoming jobs</Typography>
        ) : (
          <List disablePadding>
            {upcomingJobs.map((job, i) => (
              <React.Fragment key={job.id}>
                {i > 0 && <Divider />}
                <ListItem 
                  disablePadding 
                  sx={{ py: 1.5, cursor: 'pointer' }}
                  onClick={() => navigate(`/jobs/${job.id}`)}
                >
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight={600}>{job.location || 'No address'}</Typography>}
                    secondary={job.scheduled_date ? format(parseISO(job.scheduled_date), 'EEE dd MMM, h:mm a') : 'TBD'}
                  />
                  <Chip 
                    label={job.status} 
                    size="small" 
                    sx={{ 
                      fontSize: '0.7rem', fontWeight: 600,
                      bgcolor: STATUS_COLORS[job.status] || '#9CA3AF', color: 'white' 
                    }} 
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editing} onClose={() => setEditing(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit Team Member</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Full Name"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              fullWidth
            />
            <TextField
              label="Phone Number"
              value={editForm.phone_number}
              onChange={(e) => setEditForm({ ...editForm, phone_number: e.target.value })}
              fullWidth
            />
            <TextField
              label="Role"
              select
              value={editForm.role}
              onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
              fullWidth
            >
              <MenuItem value="worker">Worker</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setEditing(false)} sx={{ fontWeight: 600 }}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
            disabled={saving || !editForm.name}
            sx={{ fontWeight: 600 }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkerDetailView;
